import { SimLoadingSpinner } from "@xapp/components";
import { renewSession } from "auth/modules/actions";
import { Credentials } from "auth/modules/state";
import React, { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useLocation, useParams } from "react-router-dom";
import { State } from "redux/state";
import { useProfile } from "utils/useProfile";
import { ProtectedRoute, ProtectedRouteProps } from "../components/ProtectedRoute";

interface Params {
    integrationId?: string;
    appId?: string;
    organizationId?: string;
}

export interface ProtectedRouteContainerProps extends Omit<ProtectedRouteProps, "profile" | "credentials"> {
    overrides?: {
        signUp: {
            pathname: string;
            search?: string;
        };
        login: {
            pathname: string;
            search?: string;
        };
    };
}

/**
 * Wrapper around Route that ensure the user is logged in.
 */
export const ProtectedRouteContainer: FC<ProtectedRouteContainerProps> = (props) => {
    const profile = useProfile();
    const credentials = useSelector<State, Credentials>((state) => state.auth.credentials);
    const location = useLocation();
    const params = useParams<Params>();
    const dispatch = useDispatch();

    // An access token that has run out but still has a refresh token is a session we can get
    // back, not a signed out user.  On a fresh page load - which is what a deep link is - the
    // token we hydrated from local storage has almost always expired, so treating it as a
    // sign out sends the user through /login and loses the URL they asked for.  Navigating in
    // app never hits this because the running session already refreshed its token.
    const isExpired = !!credentials && new Date(credentials.Expiration).getTime() <= new Date().getTime();
    const isRenewable = isExpired && !!credentials.refreshToken;

    useEffect(() => {
        if (isRenewable) {
            dispatch(renewSession(0));
        }
    }, [dispatch, isRenewable]);

    const loginPath: { pathname: string; search?: string } = useMemo(() => {
        return props?.overrides?.login || { pathname: "/login" };
    }, [props?.overrides?.login]);

    const signUpPath: { pathname: string; search?: string } = useMemo(() => {
        // change it depending on environment

        const signUpPathName = window.xaStudioConfig.account === "studio" ? "/onboarding/sign-up" : "/sign-up";
        return props?.overrides?.signUp || { pathname: signUpPathName };
    }, [props?.overrides?.signUp]);

    const redirectToAuth = useMemo(() => {
        // If we don't have previous credentials, we will redirect them to the sign up page
        // if we just have expired credentials, we will redirect them to the login page
        // TODO: we want to also look at the original requested path and redirect based on the requested URL
        //  1. if it is {orgId}/{appId}/upgrade, redirect to login
        //  2. Or if they are requesting any path with {orgId}/{appId}, we redirect them to login
        //  let mode = !credentials
        //       ? signUpPath
        //       : new Date(credentials.Expiration).getTime() <= new Date().getTime()
        //       ? loginPath
        //       : null;

        let mode: { pathname: string; search?: string } | null = null;

        if (!credentials) {
            mode = signUpPath;
        } else if (isExpired && !isRenewable) {
            mode = loginPath;
        }

        // print the original requested path for debugging purposes
        console.log(`Original requested path: ${location.pathname}${location.search}`);
        // if we have a pathname at all and it doesn't start with /onboarding or /login or /sign-up then we redirect them to the login page
        if (
            !credentials &&
            !location.pathname.startsWith("/onboarding") &&
            !location.pathname.startsWith("/login") &&
            !location.pathname.startsWith("/sign-up")
        ) {
            console.log(`Redirecting to login page because no credentials and not on onboarding or login page.`);
            mode = loginPath;
        }

        // TODO: Remove these when we figure out our login issues
        if (!credentials) {
            console.log(`Credentials don't yet exist...`);
        }

        if (isExpired) {
            console.log(`Credentials expired.`);
        }

        const pathname = location.pathname;

        const searchParams = new URLSearchParams(location.search);
        if (params?.integrationId) {
            searchParams.set("partner", params?.integrationId);
        }

        if (mode) {
            const search = searchParams.toString();
            mode.search = search;

            console.log(`Redirecting to ${mode.pathname} with search ${search}`);

            return (
                <Redirect
                    to={{
                        ...mode,
                        state: {
                            from: {
                                pathname,
                                search,
                            },
                        },
                    }}
                />
            );
        }
        // If we are not redirecting, we will return null
        return null;
    }, [credentials, isExpired, isRenewable, location.pathname, location.search, loginPath, params?.integrationId, signUpPath]);

    // Hold the route while the session is being renewed.  Rendering the protected component
    // here would fire API calls with a dead token, and redirecting would throw the deep link away.
    if (isRenewable) {
        return <SimLoadingSpinner />;
    }

    return redirectToAuth || <ProtectedRoute profile={profile} credentials={credentials} {...props} />;
};
